import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { PageContainer } from '@/components/layout/PageContainer';
import { DetailView } from '@/components/layout/DetailView';
import { DocHeaderActions } from '@/features/common/components/DocHeaderActions';
import { useSalesOrderDetail, useSaveSalesOrder, useCreateSalesOrder, useDeleteSalesOrder } from '../api/salesOrderHooks';
import { useDocSidebar } from '@/features/common/api/useDocSidebar';
import { DocSidebar } from '@/features/common/components/DocSidebar';
import { DocChatPanel } from '@/features/common/components/DocChatPanel';
import { useDetailPageTabs } from '@/contexts/DetailPageTabsContext';
import { useDetailPageChat } from '@/contexts/DetailPageChatContext';
import { useDetailPageSidebar } from '@/contexts/DetailPageSidebarContext';
import { erp } from '@/lib/erp';
import { useAuthStore } from '@/lib/store/authStore';
import { usePreferencesStore } from '@/lib/store/preferencesStore';
import { cn } from '@/lib/utils';
import { resolveView } from '@/lib/views/registry';
import { SystemView } from '@/components/view/SystemView';
import { ExperienceView } from '@/components/view/ExperienceView';
import { StorefrontOrderBanner } from './StorefrontOrderBanner';
// FEATURE ON HOLD: QuickActions component temporarily removed from UI
// See QuickActions.tsx for implementation details
// import { QuickActions } from '@/components/view/QuickActions';
import { isDirty } from '@/features/inventory/item-detail/utils/computeDirtyState';
import { prepareDocForSave } from '@/lib/erp/save';
import { resolveOrderContext } from '@/lib/views/utils/resolveOrderContext';
import { validateViewAgainstMeta } from '@/lib/views/utils/validateViewAgainstMeta';
import type { SalesOrder } from '@/lib/erp/types/order';
import { useRegisterActiveDetailPage } from '@/hooks/useRegisterActiveDetailPage';
import { DetailPageSkeleton } from '@/features/doc/DetailPageSkeleton';
import { logger } from '@/lib/utils/logger';
import { validateChildTable } from '@/components/child-table/utils/validation';
import { validateRowLinks } from '@/components/child-table/utils/linkValidation';
import { getVisibleChildTableFields } from '@/components/child-table/utils/schemaUtils';
import { pruneLocalPlaceholderChildRowsInDoc } from '@/components/child-table/utils/prune';
import type { DocTypeSchema } from '@/lib/erp/meta';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { ChevronLeft } from 'lucide-react';
import { useDocCalculation } from '@/lib/hooks/useDocCalculation';
import { usePartyDefaults } from '@/lib/hooks/usePartyDefaults';
import { useWarning } from '@/lib/warnings';
import { PersonalizationExportMenu } from '@/features/orders/components/PersonalizationExportMenu';
import { PrintDocumentList } from './components/PrintDocumentList';
import { OrderDetailSidebar, AddressesCard } from './OrderDetailSidebar';

/**
 * Resolve Frappe's special default values to actual values
 *
 * Frappe uses special strings like "Today" in field defaults that need
 * to be converted to actual values before sending to the server.
 */
function resolveFrappeDefault(defaultValue: any, fieldtype: string): any {
  if (typeof defaultValue !== 'string') {
    return defaultValue;
  }

  // Handle date-related special defaults
  if (fieldtype === 'Date' || fieldtype === 'Datetime') {
    const lowerDefault = defaultValue.toLowerCase();
    if (lowerDefault === 'today') {
      // Return ISO date string (YYYY-MM-DD)
      return new Date().toISOString().split('T')[0];
    }
    if (lowerDefault === 'now') {
      // Return ISO datetime string
      return new Date().toISOString();
    }
  }

  // Return original value for non-special defaults
  return defaultValue;
}

export function SalesOrderDetailPage() {
  const params = useParams<{ id?: string; name?: string }>();
  const navigate = useNavigate();
  const isDesktop = useMediaQuery("(min-width: 768px)");
  // Support both /orders/:id and /sales-order/:name routes
  const salesOrderName = params.id || params.name || "";
  // NOTE: `/orders/new` is a dedicated route (no `:id` param), so params are undefined there.
  // Treat missing param as create mode.
  const isNew = (params.id === undefined && params.name === undefined) || salesOrderName === "new";
  const [uiError, setUiError] = useState<string | null>(null);

  // Chat panel state - use context to share with FloatingNav
  const { showChat, setShowChat, setOnChatClick } = useDetailPageChat();
  
  // Sidebar visibility state
  const { showSidebar, setShowSidebar } = useDetailPageSidebar();
  
  // Force hide sidebar for new documents (no sidebar data available)
  // This prevents 2-column layout from being applied
  useEffect(() => {
    if (isNew) {
      setShowSidebar(false);
    }
  }, [isNew, setShowSidebar]);

  // Get active tab from context to detect tab changes
  const { activeTab } = useDetailPageTabs();
  const previousActiveTabRef = useRef<string | null>(null);
  
  // Set chat click handler in context so FloatingNav can use it
  useEffect(() => {
    if (setOnChatClick) {
      setOnChatClick(() => () => setShowChat(!showChat));
      return () => {
        if (setOnChatClick) {
          setOnChatClick(undefined);
        }
      };
    }
  }, [setShowChat, setOnChatClick, showChat]);

  // Close chat when user switches tabs
  useEffect(() => {
    if (showChat && activeTab && previousActiveTabRef.current !== null && previousActiveTabRef.current !== activeTab) {
      setShowChat(false);
    }
    previousActiveTabRef.current = activeTab;
  }, [activeTab, showChat]);

  // Prevent body scrolling when chat is open
  useEffect(() => {
    if (showChat) {
      // Save current overflow value
      const originalOverflow = document.body.style.overflow;
      // Lock body scroll
      document.body.style.overflow = 'hidden';
      
      // Cleanup: restore original overflow when chat closes or component unmounts
      return () => {
        document.body.style.overflow = originalOverflow;
      };
    }
  }, [showChat]);

  // Get auth state — tenantCompany comes from the JWT token (set during login)
  const authStore = useAuthStore();
  const { isAuthenticated, tenantCompany } = authStore;
  
  // Query client for invalidating queries after actions
  const queryClient = useQueryClient();

  // DEC-001: View resolution
  const viewMode = usePreferencesStore((state) => state.viewMode);
  const devMode = usePreferencesStore((state) => state.devMode);
  const experienceStyle = usePreferencesStore((state) => state.experienceStyle);

  // Fetch meta schema - enabled for BOTH new and existing documents
  // Meta is needed to render the form structure regardless of document state
  // In control_plane mode, frappeClient is null (we use JWT instead), so just check isAuthenticated
  const { data: metaSchema, isLoading: isLoadingMeta } = useQuery({
    queryKey: ["meta", "Sales Order"],
    queryFn: () => erp.meta.getDocTypeSchema("Sales Order"),
    enabled: isAuthenticated, // Not dependent on isNew
  });

  // Fetch Sales Order detail (if not new)
  const { data: salesOrderData, isLoading: isLoadingDoc, error: docError } = useSalesOrderDetail(
    isNew ? "" : salesOrderName
  );

  // Resolve context from document data (after data fetch)
  const context = useMemo(
    () => resolveOrderContext(salesOrderData),
    [salesOrderData]
  );
  
  // Guard: Only allow system mode when in dev mode
  const systemMode = devMode ? viewMode === 'system' : false;

  // Resolve view
  const resolvedView = useMemo(() => {
    if (!metaSchema) return null;
    
    // Only pass preferredStyle when NOT in system mode and not 'auto'
    const preferredStyle = !systemMode && experienceStyle !== 'auto' 
      ? experienceStyle 
      : undefined;
    
    return resolveView(
      metaSchema.name,
      context,
      systemMode,
      devMode,
      preferredStyle
    );
  }, [metaSchema, context, systemMode, devMode, experienceStyle]);


  // Fieldname validation helper (dev-only)
  useEffect(() => {
    if (import.meta.env?.DEV && resolvedView?.view && metaSchema) {
      validateViewAgainstMeta(metaSchema, resolvedView.view, {
        label: 'Sales Order',
        showCoverage: true,
      });
    }
  }, [resolvedView, metaSchema]);

  // Debug: Log resolved view (remove after validation)
  useEffect(() => {
    if (resolvedView) {
      logger.log('[SalesOrderDetailPage] Resolved view:', {
        mode: resolvedView.mode,
        style: resolvedView.style,
        viewId: resolvedView.view?.id,
        viewLabel: resolvedView.view?.label,
        doctype: metaSchema?.name,
        context,
        devMode,
      });
    }
  }, [resolvedView, metaSchema, context, devMode]);

  // Track document version for child table resync
  // Bump this after successful save to resync child tables with server data
  const [docVersion, setDocVersion] = useState(0);

  // Form state
  const [formData, setFormData] = useState<Record<string, any>>({});
  // originalData reserved for future dirty state computation (when save hooks are implemented)
  const [originalData, setOriginalData] = useState<Record<string, any>>({});

  // Register this page for dev autofill
  useRegisterActiveDetailPage({
    doctype: "Sales Order",
    metaSchema: metaSchema || undefined,
    formData,
    setFormData,
  });

  // Fetch sidebar data (only for existing documents)
  // Provides: cover image, assigned users, tags, likes/comments, follow/share, attachments, history
  const sidebarQuery = useDocSidebar("Sales Order", isNew ? undefined : salesOrderName);

  // Initialize form data when Sales Order loads
  useEffect(() => {
    if (salesOrderData) {
      // Derive inclusive tax toggle from tax rows
      const taxes = (salesOrderData as Record<string, any>).taxes as Array<Record<string, any>> | undefined;
      const hasInclusive = taxes?.length
        ? taxes.every((t) => t.included_in_print_rate === 1)
        : false;
      setFormData({ ...salesOrderData, included_in_print_rate: hasInclusive ? 1 : 0 });
      setOriginalData(salesOrderData);
      // Reset docVersion when new document loads
      setDocVersion(0);
    } else if (isNew && metaSchema) {
      // Initialize with defaults for new Sales Order
      const defaults: Record<string, any> = {};
      for (const field of metaSchema.fields) {
        if (field.default !== undefined) {
          // Resolve Frappe's special default values
          defaults[field.fieldname] = resolveFrappeDefault(field.default, field.fieldtype);
        }
      }
      // Set common defaults for Sales Order
      defaults.doctype = 'Sales Order';
      defaults.docstatus = 0; // Draft
      // Company from JWT token (auth store) — required for item addition
      if (tenantCompany) {
        defaults.company = tenantCompany;
      }
      setFormData(defaults);
      setOriginalData(defaults);
      setDocVersion(0);
    }
  }, [salesOrderData, isNew, metaSchema, tenantCompany]);

  // Set default company for new Sales Orders from JWT (zero API calls)
  useEffect(() => {
    if (!isNew) return;
    const company = authStore.tenantCompany;
    if (company) setFormData(prev => (prev.company ? prev : { ...prev, company }));
  }, [isNew, authStore.tenantCompany]);

  // resetKey: docName + docVersion — handles initial load and post-save row name updates
  const resetKey = isNew ? `new-${docVersion}` : `${salesOrderName}-${docVersion}`;

  // Mutations
  const saveMutation = useSaveSalesOrder(isNew ? "" : salesOrderName, originalData);
  const createMutation = useCreateSalesOrder();
  const deleteMutation = useDeleteSalesOrder(isNew ? "" : salesOrderName);

  // Warning system - displays validation errors and messages from ERPNext
  const { showWarning } = useWarning();

  // Track previous state for rollback on server rejection
  // This ref holds the last state BEFORE the current pending change
  const previousStateRef = useRef<Record<string, unknown> | null>(null);

  // Server-side calculation — ERPNext is single source of truth
  const { calculate: triggerCalculation } = useDocCalculation({
    doctype: "Sales Order",
    debounceMs: 300,
    onSuccess: useCallback((result: import('@/lib/hooks/useDocCalculation').CalculationResult) => {
      // Update form with server-calculated values
      setFormData(prev => ({
        ...prev,
        // Totals
        total: result.total ?? prev.total,
        net_total: result.net_total ?? prev.net_total,
        total_taxes_and_charges: result.total_taxes_and_charges ?? prev.total_taxes_and_charges,
        grand_total: result.grand_total ?? prev.grand_total,
        rounded_total: result.rounded_total ?? prev.rounded_total,
        rounding_adjustment: result.rounding_adjustment ?? prev.rounding_adjustment,
        in_words: result.in_words ?? prev.in_words,
        // Quantities
        total_qty: result.total_qty ?? prev.total_qty,
        // Discounts - ALL values from server (single source of truth)
        discount_amount: result.discount_amount ?? prev.discount_amount,
        discount_percentage: result.discount_percentage ?? prev.discount_percentage,
        additional_discount_amount: result.additional_discount_amount ?? prev.additional_discount_amount,
        additional_discount_percentage: result.additional_discount_percentage ?? prev.additional_discount_percentage,
        // Update items if server provides calculated amounts
        ...(result.items && result.items.length > 0
          ? { items: prev.items?.map((item: Record<string, unknown>, idx: number) => ({
              ...item,
              ...(result.items![idx] || {}),
            })) }
          : {}),
        // Update taxes child table from server (populated from template)
        ...(result.taxes ? { taxes: result.taxes } : {}),
        // Derive inclusive tax toggle from server-returned taxes
        ...(result.taxes ? {
          included_in_print_rate: result.taxes.length > 0 &&
            result.taxes.every((t: Record<string, any>) => t.included_in_print_rate === 1) ? 1 : 0,
        } : {}),
      }));
      previousStateRef.current = null;
    }, []),
    onError: useCallback((error: string) => {
      console.error('[SalesOrderDetailPage] Server calculation failed:', error);
    }, []),
    onRejected: useCallback((lastGoodState: Record<string, unknown>, _error: string) => {
      setFormData(lastGoodState as Record<string, any>);
      previousStateRef.current = null;
    }, []),
    // Pass warnings to the warning system for display
    onWarning: showWarning,
    // Only enable calculation for draft documents (docstatus === 0)
    enabled: formData.docstatus === 0 || formData.docstatus === undefined,
  });

  // Party defaults hook - fetches currency, price list, etc. when customer is selected
  const { fetchDefaults: fetchPartyDefaults } = usePartyDefaults({
    partyType: "Customer",
    doctype: "Sales Order",
    onSuccess: useCallback((defaults: import('@/lib/hooks/usePartyDefaults').PartyDefaultsResult) => {
      // Merge defaults into form data
      setFormData(prev => {
        const updated = {
          ...prev,
          // Currency and pricing - only update if value is present
          ...(defaults.currency && { currency: defaults.currency }),
          ...(defaults.selling_price_list && { selling_price_list: defaults.selling_price_list }),
          ...(defaults.price_list_currency && { price_list_currency: defaults.price_list_currency }),
          ...(defaults.plc_conversion_rate && { plc_conversion_rate: defaults.plc_conversion_rate }),
          ...(defaults.conversion_rate && { conversion_rate: defaults.conversion_rate }),
          // Company (resolved from party or user defaults)
          ...(defaults.company && { company: defaults.company }),
          // Taxes template
          ...(defaults.taxes_and_charges && { taxes_and_charges: defaults.taxes_and_charges }),
          ...(defaults.tax_category && { tax_category: defaults.tax_category }),
          // Customer info
          ...(defaults.customer_name && { customer_name: defaults.customer_name }),
          ...(defaults.customer_group && { customer_group: defaults.customer_group }),
          ...(defaults.territory && { territory: defaults.territory }),
          // Addresses (link names + rendered HTML for sidebar display)
          ...(defaults.customer_address && { customer_address: defaults.customer_address }),
          ...(defaults.shipping_address_name && { shipping_address_name: defaults.shipping_address_name }),
          ...(defaults.address_display && { address_display: defaults.address_display }),
          ...(defaults.shipping_address && { shipping_address: defaults.shipping_address }),
          // Payment terms
          ...(defaults.payment_terms_template && { payment_terms_template: defaults.payment_terms_template }),
        };

        // Trigger calculation with updated data to recalculate items with new price list
        const mergedData = { ...prev, ...updated };
        if (prev.items && (prev.items as unknown[]).length > 0) {
          previousStateRef.current = prev;
          setTimeout(() => triggerCalculation(mergedData, prev), 0);
        }

        return mergedData;
      });
    }, [triggerCalculation]),
    onError: useCallback((error: string) => {
      console.error('[SalesOrderDetailPage] Failed to fetch party defaults:', error);
    }, []),
  });

  // Fields that trigger recalculation (and thus server validation)
  const CALCULATION_TRIGGER_FIELDS = [
    'items',
    'additional_discount_percentage',
    'additional_discount_amount',
    'discount_amount',
    'discount_percentage',
    'apply_discount_on',
    'taxes_and_charges', // Tax template - populates taxes child table
    'taxes',
    'shipping_rule', // Shipping rule - applies tiered/fixed shipping fee to taxes table
  ];

  // Fields that trigger party defaults fetch
  const PARTY_FIELDS = ['customer', 'supplier'];

  // Field change handler with server-side calculation trigger
  // CRITICAL: Captures previous state BEFORE change for rollback on rejection
  const handleFieldChange = useCallback((fieldname: string, value: any) => {
    // "Price Includes Tax" toggle — batch-sets included_in_print_rate on ALL tax rows
    if (fieldname === 'included_in_print_rate') {
      setFormData(prev => {
        const previousState = { ...prev };
        const inclusiveFlag = value ? 1 : 0;
        const updatedTaxes = (prev.taxes || []).map((row: Record<string, any>) => ({
          ...row,
          included_in_print_rate: inclusiveFlag,
        }));
        const updated = { ...prev, included_in_print_rate: value, taxes: updatedTaxes };
        previousStateRef.current = previousState;
        setTimeout(() => triggerCalculation(updated, previousState), 0);
        return updated;
      });
      return;
    }

    setFormData(prev => {
      // Capture previous state BEFORE making the change
      // This is used for rollback if server rejects
      const previousState = { ...prev };
      const updated = { ...prev, [fieldname]: value };

      // Fetch party defaults when customer/supplier changes
      if (PARTY_FIELDS.includes(fieldname) && value) {
        // Pass company from form (or auth store fallback) so backend doesn't need to resolve it
        const company = updated.company || tenantCompany || undefined;
        fetchPartyDefaults(value, company);
      }
      // Trigger server-side calculation if a calculation-relevant field changed
      else if (CALCULATION_TRIGGER_FIELDS.includes(fieldname)) {
        // Store previous state in ref for the calculation hook
        previousStateRef.current = previousState;
        // Pass both new state AND previous state for rollback capability
        setTimeout(() => triggerCalculation(updated, previousState), 0);
      }

      return updated;
    });
  }, [triggerCalculation, fetchPartyDefaults, tenantCompany]);

  // Validate form data (fail-closed: no silent skips)
  const validateForm = useMemo(() => {
    return async (dataToValidate: Record<string, any>): Promise<{ valid: boolean; errors: string[] }> => {
      const errors: string[] = [];
      
      // Fail closed: require metaSchema
      if (!metaSchema) {
        errors.push("Cannot validate: metadata schema not loaded");
        return { valid: false, errors };
      }
      
      // Find all Table fields (child tables)
      const tableFields = metaSchema.fields.filter(
        (field) => field.fieldtype === "Table" && field.options
      );
      
      // Validate each child table
      for (const field of tableFields) {
        const tableData = dataToValidate[field.fieldname];
        
        if (Array.isArray(tableData) && tableData.length > 0) {
          // Check cache first to avoid unnecessary network calls
          let childSchema: DocTypeSchema | null | undefined = queryClient.getQueryData<DocTypeSchema>([
            "meta",
            field.options,
          ]);
          
          // If not cached, try to fetch schema
          // NO COMPROMISES: We must validate or fail closed - no silent skips
          if (!childSchema) {
            try {
              childSchema = await queryClient.fetchQuery<DocTypeSchema>({
                queryKey: ["meta", field.options],
                queryFn: async () => {
                  return await erp.meta.getDocTypeSchema(field.options!);
                },
                retry: false,
              });
            } catch (schemaError) {
              // NO COMPROMISES: Fail validation if schema cannot be fetched
              // This prevents silent data integrity issues
              const errorMsg = schemaError instanceof Error ? schemaError.message : String(schemaError);
              const isPermissionError = /403|forbidden|permission/i.test(errorMsg);
              
              errors.push(
                `Cannot validate ${field.label || field.fieldname} child table: ${
                  isPermissionError
                    ? 'Permission denied. Ensure your user can read metadata (ERPNext role: System Manager or Item Manager).'
                    : `Failed to fetch schema: ${errorMsg}`
                }`
              );
              // Continue to next field - we've already added the error
              continue;
            }
          }
          
          // If schema exists but has no fields, we can't validate - fail closed
          if (!childSchema || !childSchema.fields || childSchema.fields.length === 0) {
            errors.push(
              `Cannot validate ${field.label || field.fieldname} child table: Schema has no fields available.`
            );
            continue;
          }
          
          // Get visible fields (same logic as ChildTableField)
          const visibleFields = getVisibleChildTableFields(childSchema);
          
          // Required field validation (synchronous)
          // Note: validateRow already includes row number in message format
          const requiredErrors = validateChildTable(tableData, visibleFields, field.label);
          errors.push(...requiredErrors.map((e) => e.message));
          
          // Link validation (async - only on save)
          // Parallelize validation for performance (no N+1)
          // Note: validateRowLinks now includes row number in message format
          const linkValidationPromises = tableData
            .map((row, i) => {
              if (row.__deleted) return null;
              return validateRowLinks(row, i, visibleFields);
            })
            .filter((p): p is Promise<import("@/components/child-table/types").RowValidationError[]> => p !== null);
          
          const linkValidationResults = await Promise.all(linkValidationPromises);
          for (const linkErrors of linkValidationResults) {
            errors.push(...linkErrors.map((e) => e.message));
          }
        }
      }
      
      return { valid: errors.length === 0, errors };
    };
  }, [formData, metaSchema, queryClient, isNew]);

  // Handle save
  const handleSave = async () => {
    if (!metaSchema) {
      setUiError('Cannot save: metadata not loaded');
      return;
    }

    // Validate first (using formData)
    const validation = await validateForm(formData);

    if (!validation.valid) {
      setUiError(`Please fix validation errors:\n${validation.errors.join("\n")}`);
      return;
    }

    // Prepare document for save (SINGLE SOURCE OF TRUTH)
    const docToSaveRaw = prepareDocForSave(formData, metaSchema);

    // SERVER-SIDE MANDATORY GUARD:
    // Remove local placeholder rows before save to avoid Frappe mandatory child-table errors.
    const docToSave = pruneLocalPlaceholderChildRowsInDoc(
      docToSaveRaw,
      metaSchema,
      (doctype) => queryClient.getQueryData<DocTypeSchema>(["meta", doctype])
    );

    if (isNew) {
      createMutation.mutate(docToSave, {
        onSuccess: (updatedSalesOrder: SalesOrder) => {
          // After successful create, update form data and bump docVersion
          // This triggers child table resync with server-assigned names
          setFormData(updatedSalesOrder);
          setOriginalData(updatedSalesOrder);
          setDocVersion((v) => v + 1); // Bump version to resync child tables
        },
      });
    } else {
      saveMutation.mutate(docToSave, {
        onSuccess: (updatedSalesOrder: SalesOrder) => {
          // After successful save, update form data and bump docVersion
          // This handles case where new child rows got real names from server
          setOriginalData(updatedSalesOrder);
          setFormData(updatedSalesOrder);
          setDocVersion((v) => v + 1); // Bump version to resync child tables
        },
      });
    }
  };

  // Handle delete
  const handleDelete = () => {
    deleteMutation.mutate();
  };

  // Compute dirty state
  const dirtyState = useMemo(() => {
    if (isNew) {
      // For new documents, check if any field has a value
      return Object.values(formData).some((val) => val !== null && val !== undefined && val !== "");
    }
    return isDirty(originalData, formData);
  }, [formData, originalData, isNew]);

  // Loading state — structural skeleton matching the final UI to prevent layout shift
  if (isLoadingMeta || isLoadingDoc) {
    return (
      <PageContainer className="max-w-[940px]">
        <DetailPageSkeleton doctype="Sales Order" />
      </PageContainer>
    );
  }

  // No meta schema
  if (!metaSchema) {
    return (
      <PageContainer>
        <div className="flex items-center justify-center h-64">
          <div className="text-destructive">Failed to load Sales Order schema</div>
        </div>
      </PageContainer>
    );
  }

  // Error state
  if (docError && !isNew) {
    return (
      <PageContainer>
        <div className="flex items-center justify-center h-64">
          <div className="text-destructive">Error loading Sales Order</div>
        </div>
      </PageContainer>
    );
  }

  // Get page title
  const pageTitle = isNew
    ? "New Sales Order"
    : salesOrderData?.name || "Sales Order Detail";

  const pageContent = (
    <>
      {uiError && (
        <Alert variant="destructive" className="mb-4">
          <button
            type="button"
            onClick={() => setUiError(null)}
            aria-label="Dismiss error"
            className="absolute right-4 top-4 text-destructive/70 hover:text-destructive"
          >
            ×
          </button>
          <AlertDescription>
            <pre className="whitespace-pre-wrap">{uiError}</pre>
          </AlertDescription>
        </Alert>
      )}
      {/* CRITICAL: For new documents, always use single column layout (full width) */}
      <div
        className={cn(
          "grid gap-4 sm:gap-6",
          showChat ? "items-stretch" : "items-start",
          // Always single column for new documents
          isNew 
            ? "grid-cols-1" 
            : (showSidebar && sidebarQuery.isSuccess && sidebarQuery.data && salesOrderName
                ? "grid-cols-1 lg:grid-cols-[320px,1fr]" 
                : "grid-cols-1")
        )}
        // Force single column via inline style for new docs (defensive)
        style={isNew ? { gridTemplateColumns: "1fr" } : undefined}
      >
        {/* Left: Sidebar (only for existing documents, only if successfully loaded, and if visible) */}
        {!isNew && showSidebar && salesOrderName && (sidebarQuery.isSuccess || sidebarQuery.isFetching || sidebarQuery.isError) && (
          <div className={cn(
            // When chat is open, make sidebar scrollable and constrain height
            // Use same height calculations as main content for consistency
            showChat && "md:max-h-[calc(100vh-132px-env(safe-area-inset-top,0px)-env(safe-area-inset-bottom,0px))] lg:max-h-[calc(100vh-88px-env(safe-area-inset-top,0px)-env(safe-area-inset-bottom,0px))] lg:overflow-y-auto"
          )}>
            {sidebarQuery.isError ? (
              <div className="text-destructive text-sm p-4">Failed to load sidebar</div>
            ) : sidebarQuery.data ? (
              <DocSidebar 
                doctype="Sales Order" 
                name={salesOrderName} 
                data={sidebarQuery.data} 
                editable={!isNew} 
              />
            ) : (
              <div className="text-muted-foreground text-sm p-4">Loading sidebar...</div>
            )}
          </div>
        )}

        {/* Right: Main content area */}
        <div 
          className={cn(
            showChat ? "relative min-h-0" : "relative",
            // Tablet-specific height adjustment: account for PageContainer padding (48px) + header + safe zones
            // PageContainer: p-6 = 24px top + 24px bottom = 48px
            // DetailView header: ~64px (p-6 top + p-6 bottom + content)
            // Buffer: 20px
            // Tablet (md): 132px offset + safe areas
            // Desktop (lg): 88px offset + safe areas (less padding needed)
            showChat && "md:max-h-[calc(100vh-132px-env(safe-area-inset-top,0px)-env(safe-area-inset-bottom,0px))] lg:max-h-[calc(100vh-88px-env(safe-area-inset-top,0px)-env(safe-area-inset-bottom,0px))]"
          )}
          style={showChat ? {
            // Mobile: use smaller offset, responsive classes will override for tablet/desktop
            height: 'calc(100vh - 88px - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px))',
            maxHeight: 'calc(100vh - 88px - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px))',
            overflow: 'hidden',
          } : undefined}
        >
          <DetailView
            title={isDesktop ? pageTitle : undefined}
            actions={
              <div className="flex items-center gap-3">
                {/* FEATURE ON HOLD: QuickActions temporarily removed from UI
                    Quick actions from DocViewSchema (experience mode only)
                    To re-enable: uncomment QuickActions import and restore this block
                */}
                {/* {resolvedView?.mode === 'experience' && (
                  <QuickActions
                    doctype="Sales Order"
                    documentName={!isNew ? salesOrderName : undefined}
                    formData={formData}
                    onActionSuccess={() => {
                      // Invalidate detail query to refresh data after action
                      if (!isNew && salesOrderName) {
                        queryClient.invalidateQueries({ queryKey: ["sales-order", salesOrderName] });
                      }
                    }}
                  />
                )} */}
                {!isNew && <PrintDocumentList salesOrder={salesOrderName} />}
                {!isNew && <PersonalizationExportMenu salesOrder={salesOrderName} />}
                <DocHeaderActions
                  doctype="Sales Order"
                  docName={!isNew ? salesOrderName : undefined}
                  onSave={handleSave}
                  onPermanentDelete={!isNew ? handleDelete : undefined}
                  isPermanentDeleting={deleteMutation?.isPending}
                  isSaving={saveMutation.isPending || createMutation.isPending}
                  isDirty={dirtyState}
                  isNew={isNew}
                />
              </div>
            }
            disableScroll={showChat}
          >
            {showChat ? (
              // Chat mode: main area is Chat & Activity
              !isNew && salesOrderName ? (
                <div className="h-full w-full flex min-h-0">
                  <DocChatPanel
                    doctype="Sales Order"
                    name={salesOrderName}
                    onClose={() => setShowChat(false)}
                  />
                </div>
              ) : (
                <div className="text-muted-foreground text-sm">
                  Chat is only available after the Sales Order is created.
                </div>
              )
            ) : (
              // DEC-001: Normal mode: show System or Experience view
              <>
              {!isNew && (!activeTab || activeTab === 'details') && <StorefrontOrderBanner formData={formData} salesOrderName={salesOrderName} />}
              {/* New orders: show address card inline when customer has address data */}
              {isNew && (formData.shipping_address || formData.address_display) && (
                <div className="mb-4">
                  <AddressesCard formData={formData} onFieldChange={handleFieldChange} salesOrderName="" />
                </div>
              )}
              <div className={cn(!isNew && (!activeTab || activeTab === 'details') && "flex flex-col xl:flex-row gap-6")}>
                <div className={cn(!isNew && (!activeTab || activeTab === 'details') && "flex-1 min-w-0")}>
                  {resolvedView ? (
                    resolvedView.mode === 'system' ? (
                      <SystemView
                        meta={metaSchema}
                        formData={formData}
                        onFieldChange={handleFieldChange}
                        disabled={false} // TODO: Set based on permissions
                        onSave={handleSave}
                        isSaving={false} // TODO: Track saving state
                        resetKey={resetKey}
                      />
                    ) : resolvedView.view ? (
                      <ExperienceView
                        view={resolvedView.view}
                        meta={metaSchema}
                        formData={formData}
                        onFieldChange={handleFieldChange}
                        disabled={false} // TODO: Set based on permissions
                        onSave={handleSave}
                        isSaving={false} // TODO: Track saving state
                        resetKey={resetKey}
                        documentName={isNew ? undefined : salesOrderName}
                        isNew={isNew}
                      />
                    ) : (
                      <div className="text-muted-foreground">No view available</div>
                    )
                  ) : (
                    <div className="text-muted-foreground">Loading view configuration...</div>
                  )}
                </div>
                {!isNew && (!activeTab || activeTab === 'details') && (
                  <div className="w-full xl:w-[380px] shrink-0 order-first xl:order-last">
                    {/* Mobile (<xl): show the info cards FIRST (above the order
                        content) so they're immediately reachable. Stacking them
                        below ~3000px of order content buried them at the bottom of
                        the mobile scroll area (effectively missing on phones).
                        Desktop keeps them on the right via xl:order-last. */}
                    <OrderDetailSidebar formData={formData} onFieldChange={handleFieldChange} />
                  </div>
                )}
              </div>
              </>
            )}
          </DetailView>
        </div>
      </div>
    </>
  );

  if (!isDesktop) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-background">
        {/* Mobile back header — flush with ErpLayout header */}
        <div className="sticky top-0 z-10 flex items-center gap-2 h-12 px-3 bg-white dark:bg-gray-950 border-b border-border/40">
          <button
            type="button"
            onClick={() => navigate("/orders")}
            className="flex items-center gap-1 text-primary text-sm font-medium"
          >
            <ChevronLeft className="w-5 h-5" />
            คำสั่งซื้อ
          </button>
          <span className="flex-1 text-center text-sm font-semibold pr-12">
            {isNew ? "คำสั่งซื้อใหม่" : (salesOrderData?.name || "คำสั่งซื้อ")}
          </span>
        </div>
        <div className="px-2 py-3 pb-24">
          {pageContent}
        </div>
      </div>
    );
  }

  return (
    <PageContainer>
      {pageContent}
    </PageContainer>
  );
}

